import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '../components/Icon'
import { languageFromPath, withLang } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

// The Nimvoy Promise — refund / money-back guarantee page. Content lives in
// src/i18n/locales/{lang}/promise.json (English authored; other locales fall
// back to English via i18next fallbackLng). JSX reuses the About/FAQ visual
// language (numbered stamps, alternating bg, dark CTA card) so no new CSS.

interface CaseData {
  title: string
  body: string
}
interface BulletData {
  title: string
  body: string
}

export default function RefundPolicy() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('promise')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.title'), description: t('seo.description') })

  const cases = t('cases', { returnObjects: true }) as CaseData[]
  const bullets = t('how.bullets', { returnObjects: true }) as BulletData[]

  return (
    <div className="faq">
      <section className="faq-hero">
        <div className="faq-hero-inner">
          <div className="breadcrumb">
            <Link to={withLang('/', lang)} style={{ color: 'var(--ink-3)' }}>
              {t('nav.home', { ns: 'common' })}
            </Link>{' '}
            / <span style={{ color: 'var(--ink)' }}>{t('breadcrumb')}</span>
          </div>
          <div className="eyebrow" style={{ marginTop: 16 }}>{t('hero.eyebrow')}</div>
          <h1 className="faq-hero-h1">
            {t('hero.titlePart1')}<em>{t('hero.titleEmphasis')}</em>
          </h1>
          <p className="lede faq-hero-lede">{t('hero.lede')}</p>
        </div>
      </section>

      {cases.map((c, idx) => (
        <section
          key={idx}
          className="section how-deep faq-deep"
          style={{ background: idx % 2 === 0 ? 'var(--bg-sunk)' : 'var(--bg)' }}
        >
          <div className="section-inner">
            <div className="how-deep-head">
              <div className={`how-deep-stamp n${(idx % 3) + 1}`}>0{idx + 1}</div>
              <div>
                <h2 className="h1">{c.title}</h2>
              </div>
            </div>
            <div className="about-section-body">
              <p className="about-body">{c.body}</p>
            </div>
          </div>
        </section>
      ))}

      <section className="section how-deep faq-deep" style={{ background: 'var(--bg-sunk)' }}>
        <div className="section-inner">
          <div className="how-deep-head">
            <div className="how-deep-stamp n1">0{cases.length + 1}</div>
            <div>
              <div className="eyebrow" style={{ marginBottom: 8 }}>{t('how.eyebrow')}</div>
              <h2 className="h1">{t('how.title')}</h2>
            </div>
          </div>
          <div className="about-section-body">
            <ul className="about-list">
              {bullets.map((b) => (
                <li key={b.title}>
                  <strong>{b.title}.</strong> {b.body}
                </li>
              ))}
            </ul>
            <p className="about-body muted" style={{ marginTop: 24, fontSize: 14 }}>
              {t('finePrint')}
            </p>
          </div>
        </div>
      </section>

      <section className="section how-cta">
        <div className="section-inner">
          <div className="how-cta-card faq-cta-card">
            <div className="how-cta-body" style={{ padding: '56px' }}>
              <div className="eyebrow how-cta-eyebrow">{t('cta.eyebrow')}</div>
              <h2 className="h1 how-cta-h1">
                {t('cta.titlePart1')}<em>{t('cta.titleEmphasis')}</em>
              </h2>
              <p className="lede how-cta-lede">{t('cta.lede')}</p>
              <div className="how-cta-row">
                <button
                  type="button"
                  className="btn primary lg"
                  onClick={() => navigate(withLang('/destinations', lang))}
                >
                  {t('cta.primary')} <Icon name="arrow" size={14} />
                </button>
                <Link to={withLang('/faq', lang)} className="btn ghost lg">
                  {t('cta.ghost')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
